import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { descargarArchivo } from "../../utils/download";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Plus, 
  Search, 
  Truck,
  Edit,
  Trash2,
  MessageCircle,
  Download
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const Proveedores = () => {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    categoria: "",
    whatsapp: "",
    telefono: "",
    correo: "",
    costo_base: "",
    notas: ""
  });

  const categorias = [
    { value: "catering", label: "Catering" },
    { value: "decoracion", label: "Decoración" },
    { value: "sonido", label: "Sonido" },
    { value: "mobiliario", label: "Mobiliario" },
    { value: "flores", label: "Flores" },
    { value: "pasteleria", label: "Pastelería" },
    { value: "fotografia", label: "Fotografía" },
    { value: "video", label: "Video" },
    { value: "musica", label: "Música" },
    { value: "iluminacion", label: "Iluminación" },
    { value: "otro", label: "Otro" },
  ];

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/proveedores`);
      setProveedores(response.data);
    } catch {
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    if (!formData.nombre || !formData.categoria) {
      toast.error("Nombre y categoría son requeridos");
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        costo_base: formData.costo_base ? parseFloat(formData.costo_base) : null
      };

      if (editingProveedor) {
        await axios.put(`${API}/proveedores/${editingProveedor.proveedor_id}`, dataToSend);
        toast.success("Proveedor actualizado");
      } else {
        await axios.post(`${API}/proveedores`, dataToSend);
        toast.success("Proveedor agregado");
      }
      resetForm();
      await fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/proveedores/${deleteId}`);
      toast.success("Proveedor eliminado");
      await fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    } finally {
      setDeleteId(null);
    }
  };

  const openEditDialog = (p) => {
    setEditingProveedor(p);
    setFormData({
      nombre: p.nombre,
      categoria: p.categoria,
      whatsapp: p.whatsapp || "",
      telefono: p.telefono || "",
      correo: p.correo || "",
      costo_base: p.costo_base?.toString() || "",
      notas: p.notas || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProveedor(null);
    setFormData({
      nombre: "",
      categoria: "",
      whatsapp: "",
      telefono: "",
      correo: "",
      costo_base: "",
      notas: ""
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getCategoriaLabel = (cat) => {
    return categorias.find(c => c.value === cat)?.label || cat;
  };

  const getCategoriaColor = (cat) => {
    const colors = {
      catering: "bg-amber-100 text-amber-800",
      decoracion: "bg-pink-100 text-pink-800",
      sonido: "bg-blue-100 text-blue-800",
      mobiliario: "bg-stone-100 text-stone-800",
      flores: "bg-rose-100 text-rose-800",
      pasteleria: "bg-purple-100 text-purple-800",
      fotografia: "bg-cyan-100 text-cyan-800",
      video: "bg-indigo-100 text-indigo-800",
      musica: "bg-emerald-100 text-emerald-800",
    };
    return colors[cat] || "bg-stone-100 text-stone-800";
  };

  const handleDescargarPDF = async () => {
    toast.info("Generando PDF de proveedores...");
    try {
      const response = await axios.get(`${API}/admin/exportar/pdf/proveedores`);
      descargarArchivo(response.data.url, response.data.filename);
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error al generar PDF");
    }
  };

  const filteredProveedores = proveedores.filter(p => {
    const matchesSearch = p.nombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCat = filterCategoria === "todos" || p.categoria === filterCategoria;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-stone-900">Proveedores</h1>
          <p className="text-stone-600 mt-1">Directorio de proveedores de servicios</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDescargarPDF} className="border-stone-300" data-testid="btn-pdf-proveedores">
            <Download className="w-4 h-4 mr-2" />
            Descargar PDF
          </Button>
          <Button onClick={openNewDialog} className="btn-primary" data-testid="btn-nuevo-proveedor">
            <Plus className="w-4 h-4 mr-2" />
            Agregar Proveedor
          </Button>
        </div>
      </div>

      {/* Stats by Category */}
      <div className="flex flex-wrap gap-2">
        {categorias.slice(0, 6).map(cat => {
          const count = proveedores.filter(p => p.categoria === cat.value).length;
          if (count === 0) return null;
          return (
            <span 
              key={cat.value} 
              className={`badge ${getCategoriaColor(cat.value)} cursor-pointer`}
              onClick={() => setFilterCategoria(cat.value)}
            >
              {cat.label}: {count}
            </span>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input 
            placeholder="Buscar proveedor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-stone-300"
            data-testid="search-proveedores"
          />
        </div>
        <Select value={filterCategoria} onValueChange={setFilterCategoria}>
          <SelectTrigger className="w-full md:w-48 border-stone-300" data-testid="filter-categoria">
            <SelectValue placeholder="Filtrar por categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas las categorías</SelectItem>
            {categorias.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Proveedores List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredProveedores.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProveedores.map(p => (
            <Card key={p.proveedor_id} className="border-stone-200 hover:shadow-lg transition-shadow" data-testid={`proveedor-card-${p.proveedor_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#C89B7B]/20 rounded-full flex items-center justify-center">
                      <Truck className="w-6 h-6 text-[#C89B7B]" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-900">{p.nombre}</p>
                      <span className={`badge ${getCategoriaColor(p.categoria)}`}>
                        {getCategoriaLabel(p.categoria)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  {p.whatsapp && (
                    <div className="flex items-center gap-2 text-stone-600">
                      <MessageCircle className="w-4 h-4" />
                      {p.whatsapp}
                    </div>
                  )}
                  {p.correo && (
                    <div className="text-stone-500 text-xs truncate">{p.correo}</div>
                  )}
                  {p.costo_base && (
                    <div className="flex justify-between">
                      <span className="text-stone-500">Costo base:</span>
                      <span className="font-medium">${p.costo_base?.toLocaleString()}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => openEditDialog(p)}
                    className="border-stone-300 text-xs flex-1"
                    data-testid={`btn-editar-${p.proveedor_id}`}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                  {p.whatsapp && (
                    <a 
                      href={`https://wa.me/52${p.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="outline" size="sm" className="border-stone-300">
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    </a>
                  )}
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setDeleteId(p.proveedor_id)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Truck className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="font-display text-xl text-stone-900 mb-2">No hay proveedores</h3>
          <p className="text-stone-600">Agrega tus proveedores de servicios</p>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingProveedor ? "Editar Proveedor" : "Agregar Proveedor"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input 
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                className="border-stone-300"
                data-testid="input-nombre-proveedor"
              />
            </div>
            <div className="space-y-2">
              <Label>Categoría *</Label>
              <Select 
                value={formData.categoria} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, categoria: v }))}
              >
                <SelectTrigger className="border-stone-300" data-testid="select-categoria-proveedor">
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="whatsapp">WhatsApp</Label>
                <Input 
                  id="whatsapp"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                  className="border-stone-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono</Label>
                <Input 
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                  className="border-stone-300"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="correo">Correo</Label>
              <Input 
                id="correo"
                type="email"
                value={formData.correo}
                onChange={(e) => setFormData(prev => ({ ...prev, correo: e.target.value }))}
                className="border-stone-300"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="costo_base">Costo base ($)</Label>
              <Input 
                id="costo_base"
                type="number"
                value={formData.costo_base}
                onChange={(e) => setFormData(prev => ({ ...prev, costo_base: e.target.value }))}
                className="border-stone-300"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notas">Notas</Label>
              <Input 
                id="notas"
                value={formData.notas}
                onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
                className="border-stone-300"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-stone-300">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="btn-primary" data-testid="btn-guardar-proveedor">
              {editingProveedor ? "Actualizar" : "Agregar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar proveedor?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Proveedores;
